import { useCallback, useState } from 'react';

/**
 * The three — and only three — reachable tabs of `/user_settings/`.
 * `submittal` is the default for an empty OR unknown fragment.
 */
export type UserSettingsTab = 'submittal' | 'proposal' | 'color';

/**
 * ★★ THE HASH CONTRACT — READ ONCE, THEN WRITE-ONLY. DO NOT "FIX" THIS. ★★
 *
 * The task brief for this port originally said hash routing was live here and
 * that `controller/router.js` routed `:tab` → `changePrjAppState` behind a
 * `get.permissions` promise. DISCOVER proved that wrong and this hook follows
 * DISCOVER (§3 "THE BRIEF'S PREMISE IS WRONG"):
 *
 *  - `mvc/controller/router.js` is DEAD CODE. `grep -rn "router\|Router"` over
 *    the whole `mvc/` tree returns exactly two hits: the requirejs path alias
 *    at `app.js:35` and the file itself. It is never `require`d and never
 *    instantiated, so `fn['get.permissions']` never runs (and does not even
 *    exist on `fn` — the real key is `permissions.all`).
 *  - `app.js:369-372` still calls `Backbone.history.start()`, but with ZERO
 *    registered routes. Its only surviving effect is that it makes
 *    `Backbone.history.navigate()` start working.
 *
 * The three consequences this hook reproduces exactly:
 *
 *  1. THE INITIAL HASH IS READ ONCE, AT BOOT. `view/main.js:64-70` is a plain
 *     `if/else if/else` over `window.location.hash`, evaluated a single time
 *     inside `initialize`. `#proposal` → proposal, `#color` → colour, and
 *     EVERYTHING else → submittal: empty, `#zzz`, `#project`, even `#Submittal`
 *     (the comparison is case-sensitive `==` against the exact strings). There
 *     is no unknown-fragment error path — `Dispatcher.trigger('open:zzz')` has
 *     no listener, which is why the `else` branch simply wins. The `useState`
 *     lazy initialiser below IS that one-time read.
 *
 *  2. A TAB CLICK WRITES THE HASH AND PUSHES A HISTORY ENTRY.
 *     `api/api.js:74,92,111` call `Backbone.history.navigate(tab)`, which with
 *     hashChange transport assigns `location.hash = '#' + fragment` — a real
 *     history entry, not a replace. `navigate` early-returns when the fragment
 *     is unchanged (`if (this.fragment === fragment) return`), so re-clicking
 *     the already-active tab adds nothing; the `!==` guard below is that.
 *
 *  3. ★ BACK / FORWARD / A MANUAL `location.hash =` CHANGE THE URL BUT NOT THE
 *     VIEW. With no routes registered there is nothing for `checkUrl` to load,
 *     so the tab, the document title, the body class and the `.selected` rail
 *     item all stay where they were — verified live in DISCOVER §3. THIS HOOK
 *     THEREFORE DELIBERATELY DOES NOT SUBSCRIBE TO `hashchange`, `popstate`, or
 *     react-router's `useLocation()`. Adding any of those would "fix" a
 *     behaviour the port is required to mirror.
 *
 * ★ WHY `window.location.hash` AND NOT `useNavigate()`.
 * The project board writes its tab hash through the router (`ProjectBoardPage
 * .tsx:163-166`) because it READS the hash back through `useLocation`. Here the
 * read side must not exist at all, so routing the write through the router
 * would only add a location subscription that could feed back into the tab
 * state. A bare assignment is also literally what `Backbone.History._updateHash`
 * does. The router picks the URL back up on the next `popstate` (i.e. on Back),
 * at which point — correctly — nothing on this page reacts.
 *
 * ★ NOT PORTED: the boot-time hash WRITE. `open_submittal` etc. call
 * `Backbone.history.navigate(...)` on the boot path too, but
 * `Backbone.History.prototype.navigate` starts with `if (!History.started)
 * return false`, and `app.js:369-372` only calls `Backbone.history.start()`
 * AFTER `create.view.main` has resolved (i.e. after `initialize` has already
 * triggered the opening tab). So landing on `/user_settings/` leaves the URL
 * hash-less until the user clicks a tab — which is exactly what makes quirk Q2
 * (arrow keys throw with no hash) reachable at all. `openTab` below is called
 * only from a click handler, never on mount, so this holds.
 */
const readInitialTab = (): UserSettingsTab => {
  // `view/main.js:64-70`, verbatim: two exact-string comparisons and an `else`.
  if (window.location.hash === '#proposal') return 'proposal';
  if (window.location.hash === '#color') return 'color';
  return 'submittal';
};

export function useUserSettingsTab() {
  const [tab, setTab] = useState<UserSettingsTab>(readInitialTab);

  const openTab = useCallback((next: UserSettingsTab) => {
    // `activateTab` → `Dispatcher.trigger('open:' + tabName)` → `api.js`
    // `open_<tab>`: swap the visible pane FIRST, then navigate.
    setTab(next);
    // `Backbone.history.navigate(next)` — including its no-op-when-unchanged
    // guard, so clicking the active tab adds no history entry.
    if (window.location.hash !== `#${next}`) window.location.hash = next;
  }, []);

  return { tab, openTab };
}
